// FASE 05 — página de precificação de uma Tabela de Preços.
// Planos: docs/19-08-2026 (entrega inicial) e docs/26-08-2026 (reteste do PO de 26/08).
//
// Regras que valem também no servidor (nunca só aqui):
// - empresa com a chave "apenas para consulta" não grava nada;
// - "Homologar" exige tabela Em Edição E ao menos um preço gravado (TP-8);
// - "Disponibilizar Mercado" é exclusivo do perfil MASTER (item 17);
// - salvar fora de "Em Edição" devolve a tabela para "Em Edição" (TP-4);
// - hoje só COMPONENTES e VIDROS têm onde guardar preço no banco do legado.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { useNavigation } from "contexts/notificationsContext";
import { useAuth } from "shared/hooks/auth";
import {
  IEstadoImportacao,
  IItemPrecificacao,
  ILogImportacaoLinha,
  IPrecificacao,
  IPrecoAlterado,
  OperacaoMassa,
  SituacaoTabelaPrecoEnum,
  StatusProdutoEnum,
  STATUS_PRODUTO_LABELS,
} from "../types/tabelaPreco.types";
import { MaterialEnum } from "../types/materialEnum";
import {
  alterarPrecosMassa,
  concluirHomologacaoTabela,
  desfazerImportacao,
  disponibilizarTabela,
  exportarPrecificacao,
  homologarTabela,
  importarPlanilha,
  obterConsultaPublicada,
  obterEstadoImportacao,
  obterPrecificacao,
  salvarPrecos,
} from "../services/tabelaPrecoService";
import { ModalAlterarPrecos } from "./components/ModalAlterarPrecos";
import { ModalConfirmacao } from "./components/ModalConfirmacao";
import { ModalImportar } from "./components/ModalImportar";
import { CelulaPreco } from "./components/CelulaPreco";
import { baixarPlanilhaPrecos, planilhaParaJson } from "./helpers/planilhaPrecos";

const COR_PRIMARIA = "#3C90AD";
const TAMANHO_PAGINA_PADRAO = 50;

// Textos definidos pelo PO no reteste de 26/08 (item 15 e TP-4).
const MSG_HOMOLOGAR =
  "A tabela de preços ficará com a situação Em Homologação para a validação no CEM. " +
  "Após a validação, acesse o MOF > Tabela de Preços e acione o botão Concluir Homologação. Deseja continuar?";
const MSG_CONCLUIR = "A tabela de preços ficará com a situação Homologada. Deseja continuar?";
const MSG_DISPONIBILIZAR =
  "A tabela de preços ficará disponível para uso dos clientes no software CEM. Deseja continuar?";
const MSG_SALVAR_VOLTA_EDICAO =
  "As alterações realizadas serão gravadas e a tabela de preços ficará na situação Em Edição. Deseja continuar?";

// Plano 14/10/2026, Frente 2 (decisão P2 do PO): planilha × edição manual são exclusivas.
// Mesmos textos que o servidor devolve ao recusar.
const MSG_PLANILHA_PENDENTE =
  "Há uma planilha importada em andamento nesta tabela. Disponibilize a tabela ou exclua a planilha para editar os preços manualmente.";
const MSG_EDICAO_MANUAL_PENDENTE =
  "Há alterações manuais em andamento nesta tabela. Disponibilize a tabela para poder importar uma planilha.";

const corDaSituacao = (situacao: SituacaoTabelaPrecoEnum): { bg: string; fg: string } => {
  switch (situacao) {
    case SituacaoTabelaPrecoEnum.EmEdicao:
      return { bg: "#FFF3E0", fg: "#E65100" };
    case SituacaoTabelaPrecoEnum.EmHomologacao:
      return { bg: "#E3F2FD", fg: "#1565C0" };
    case SituacaoTabelaPrecoEnum.Homologada:
      return { bg: "#EDE7F6", fg: "#4527A0" };
    case SituacaoTabelaPrecoEnum.Disponivel:
      return { bg: "#E8F5E9", fg: "#2E7D32" };
    default:
      return { bg: "#F5F5F5", fg: "#424242" };
  }
};

export const TabelaPrecoEdicao: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const idTabelaPreco = Number(id);
  const navigate = useNavigate();
  const { openNotification } = useNavigation();
  const { havePermission } = useAuth();

  // Item 17: mesmo gate de Produtos N1 e Aplicabilidades.
  const isMaster = havePermission(["Master"]);

  // Modo consulta: switch "Modo edição" desligado na listagem (item 7).
  const modoConsulta = searchParams.get("consulta") === "1";

  const [precificacao, setPrecificacao] = useState<IPrecificacao | null>(null);
  const [itens, setItens] = useState<IItemPrecificacao[]>([]);
  const [totalItens, setTotalItens] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erroCarga, setErroCarga] = useState<string | undefined>();
  const [selecionados, setSelecionados] = useState<GridSelectionModel>([]);

  // Paginação e filtros resolvidos no servidor (TP-6).
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_PADRAO);
  const [filtros, setFiltros] = useState<{
    codigo: string;
    descricao: string;
    fornecedor: string;
    linha: string;
    tipoKit: string;
    status: number | "";
  }>({ codigo: "", descricao: "", fornecedor: "", linha: "", tipoKit: "", status: StatusProdutoEnum.Ativo });

  const [modalAlterarAberto, setModalAlterarAberto] = useState(false);
  const [modalImportarAberto, setModalImportarAberto] = useState(false);
  const [modalDesfazerAberto, setModalDesfazerAberto] = useState(false);
  const [modalHomologarAberto, setModalHomologarAberto] = useState(false);
  const [modalConcluirAberto, setModalConcluirAberto] = useState(false);
  const [modalDisponibilizarAberto, setModalDisponibilizarAberto] = useState(false);
  const [modalSalvarAberto, setModalSalvarAberto] = useState(false);

  // Plano 14/10/2026, Frentes 2 e 3: planilha pendente (lixeira) × edição manual pendente.
  const [estadoImportacao, setEstadoImportacao] = useState<IEstadoImportacao | null>(null);

  // Delta do que foi digitado: chave do grid -> preço alterado. Salvamos só isto.
  const alteracoes = useRef<Map<string, IPrecoAlterado>>(new Map());
  const [temAlteracoes, setTemAlteracoes] = useState(false);

  const limparAlteracoes = useCallback(() => {
    alteracoes.current = new Map();
    setTemAlteracoes(false);
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const consulta = {
        pagina,
        tamanhoPagina,
        codigo: filtros.codigo,
        descricao: filtros.descricao,
        fornecedor: filtros.fornecedor,
        linha: filtros.linha,
        tipoKit: filtros.tipoKit,
        status: filtros.status === "" ? null : filtros.status,
      };

      // O estado da importação vem junto em toda recarga: é ela que roda depois de salvar,
      // importar, desfazer e das transições — e a trava tem que acompanhar.
      const [{ dados, erro }, estado] = await Promise.all([
        modoConsulta
          ? obterConsultaPublicada(idTabelaPreco, consulta)
          : obterPrecificacao(idTabelaPreco, consulta),
        modoConsulta ? Promise.resolve(null) : obterEstadoImportacao(idTabelaPreco),
      ]);

      setEstadoImportacao(estado);
      setPrecificacao(dados);
      setItens(dados?.itens ?? []);
      setTotalItens(dados?.totalItens ?? 0);
      setErroCarga(erro);
      limparAlteracoes();
    } finally {
      setCarregando(false);
    }
  }, [idTabelaPreco, limparAlteracoes, modoConsulta, pagina, tamanhoPagina, filtros]);

  useEffect(() => {
    if (Number.isFinite(idTabelaPreco) && idTabelaPreco > 0) carregar();
  }, [carregar, idTabelaPreco]);

  const podeEditar = (precificacao?.podeEditar ?? false) && !modoConsulta;
  const planilhaPendente = estadoImportacao?.planilhaPendente ?? null;
  const edicaoManualPendente = estadoImportacao?.edicaoManualPendente ?? false;
  // Plano 14/10/2026, Frente 2: com planilha pendente os preços não se editam à mão.
  const podeEditarPrecos = podeEditar && !planilhaPendente;
  const tipoMaterial = precificacao?.tipoMaterial;
  const ehVidro = tipoMaterial === MaterialEnum.VIDRO;
  const ehComponente = tipoMaterial === MaterialEnum.COMPONENTE;
  const ehPerfil = tipoMaterial === MaterialEnum.PERFIL;
  const ehKit = tipoMaterial === MaterialEnum.KIT;
  const ehSuperficieOuAcabamento =
    tipoMaterial === MaterialEnum.SUPERFICIE || tipoMaterial === MaterialEnum.ACABAMENTO;
  const situacao = precificacao?.situacao;

  const aplicarFiltro = useCallback((campo: keyof typeof filtros, valor: string | number) => {
    setPagina(0);
    setFiltros((f) => ({ ...f, [campo]: valor }));
  }, []);

  const registrarAlteracao = useCallback((item: IItemPrecificacao, novoPreco: number) => {
    // Plano 30/08/2026: a chave do preço deixou de ser só o produto. Em Kits ela inclui a
    // combinação medida+cor (TP-18); em Acabamentos anda junto a Taxa (TP-20). Reaproveitamos
    // o que já está na linha para não perder o valor gravado ao alterar só o preço.
    const anterior = alteracoes.current.get(item.chave);
    alteracoes.current.set(item.chave, {
      idProduto: item.idProduto,
      cor: item.cor,
      preco: novoPreco,
      taxa: anterior?.taxa !== undefined ? anterior.taxa : item.taxa,
      idMedidaCores: item.idMedidaCores,
      idCor: item.idCor,
      tamanhoBarra: item.tamanhoBarra,
    });
    setTemAlteracoes(true);

    setItens((anteriores) =>
      anteriores.map((linha) =>
        linha.chave === item.chave
          ? {
              ...linha,
              preco: novoPreco,
              precoItem:
                linha.quantidadeEmbalagem > 0
                  ? Number((novoPreco / linha.quantidadeEmbalagem).toFixed(4))
                  : novoPreco,
            }
          : linha
      )
    );
  }, []);

  // Plano 30/08/2026, Frente 10 (TP-20): a Taxa dos Acabamentos é um segundo valor digitável na
  // mesma linha. Grava junto com o preço — por isso repete o preço corrente na alteração.
  const registrarAlteracaoTaxa = useCallback((item: IItemPrecificacao, novaTaxa: number) => {
    const anterior = alteracoes.current.get(item.chave);
    alteracoes.current.set(item.chave, {
      idProduto: item.idProduto,
      cor: item.cor,
      preco: anterior?.preco ?? item.preco,
      taxa: novaTaxa,
      idMedidaCores: item.idMedidaCores,
      idCor: item.idCor,
      tamanhoBarra: item.tamanhoBarra,
    });
    setTemAlteracoes(true);

    setItens((anteriores) =>
      anteriores.map((linha) =>
        linha.chave === item.chave ? { ...linha, taxa: novaTaxa } : linha
      )
    );
  }, []);

  // O usuário pode digitar pelo "$ Item": o preço da embalagem é recalculado a partir dele.
  const registrarAlteracaoPorItem = useCallback(
    (item: IItemPrecificacao, novoPrecoItem: number) => {
      const quantidade = item.quantidadeEmbalagem > 0 ? item.quantidadeEmbalagem : 1;
      registrarAlteracao(item, Number((novoPrecoItem * quantidade).toFixed(4)));
    },
    [registrarAlteracao]
  );

  const colunas: GridColDef[] = useMemo(() => {
    // Reporte TP de 02/09 (item 2): a coluna Descrição SAIU do grid — a descrição vira hint no
    // hover do código ("CÓDIGO — DESCRIÇÃO"), e o código aparece completo, sem truncar.
    const base: GridColDef[] = [
      {
        field: "codigo",
        headerName: "Código",
        flex: 1.4,
        minWidth: 180,
        renderHeader: () => (
          <FilterColumn
            label="Código"
            name="codigo"
            value={filtros.codigo}
            onChange={(e) => aplicarFiltro("codigo", e.target.value)}
          />
        ),
        renderCell: (params) => (
          <Tooltip title={`${params.row.codigo} — ${params.row.descricao || ""}`} placement="top-start">
            <span style={{ whiteSpace: "nowrap" }}>{params.row.codigo}</span>
          </Tooltip>
        ),
      },
    ];

    if (ehVidro) {
      base.push({ field: "cor", headerName: "Cor", flex: 0.8, minWidth: 120 });
    }

    // Plano 30/08/2026, Frente 8 (reporte do TI de 30/08 / decisão TP-19): Componentes,
    // Superfícies e Kits ganham a coluna Cor LOGO APÓS a Descrição. Em Vidros a Cor continua
    // antes da Descrição — lá ela faz parte da chave do preço (uma linha por código+cor),
    // aqui é só exibição: o preço segue único por código.
    if (ehComponente || tipoMaterial === MaterialEnum.SUPERFICIE || ehKit) {
      base.push({ field: "cor", headerName: "Cor", flex: 0.8, minWidth: 120 });
    }

    base.push(
      {
        field: "fornecedor",
        headerName: "Fornecedor",
        flex: 0.9,
        minWidth: 150,
        renderHeader: () => (
          <FilterColumn
            label="Fornecedor"
            name="fornecedor"
            value={filtros.fornecedor}
            onChange={(e) => aplicarFiltro("fornecedor", e.target.value)}
          />
        ),
      }
    );

    // Colunas novas do plano de 28/08 (TP-16), por tipo.
    if (ehPerfil) {
      base.push(
        {
          field: "linha",
          headerName: "Linha/Sistema",
          flex: 1,
          minWidth: 160,
          renderHeader: () => (
            <FilterColumn
              label="Linha/Sistema"
              name="linha"
              value={filtros.linha}
              onChange={(e) => aplicarFiltro("linha", e.target.value)}
            />
          ),
        },
        { field: "peso", headerName: "Peso", width: 90, type: "number" },
        // Plano 08/09/2026 (pedido do TI): tamanho da barra logo após Peso. Só perfis com
        // unidade BR trazem valor — e cada tamanho é uma linha própria, com preço próprio.
        {
          field: "tamanhoBarra",
          headerName: "Tamanho da barra",
          width: 150,
          type: "number",
          sortable: false,
          renderCell: (params) => (params.row.tamanhoBarra ?? "") === "" ? "—" : params.row.tamanhoBarra,
        },
        { field: "unidadeItem", headerName: "Unidade", width: 100, sortable: false }
      );
    }

    if (ehKit) {
      base.push({
        field: "tipoKit",
        headerName: "Tipo",
        width: 160,
        sortable: false,
        // A coluna "L x H" entra logo depois desta — ver o push abaixo do bloco.
        renderHeader: () => (
          <Select
            size="small"
            variant="standard"
            displayEmpty
            value={filtros.tipoKit}
            onChange={(e) => aplicarFiltro("tipoKit", e.target.value as string)}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: "100%", fontSize: "0.8rem" }}
          >
            <MenuItem value="">Tipo: todos</MenuItem>
            <MenuItem value="Ferragem">Ferragem</MenuItem>
            <MenuItem value="Engenharia">Engenharia</MenuItem>
          </Select>
        ),
      });

      // Plano 30/08/2026, Frente 11 (reporte do TI / decisão TP-18): medidas cadastradas do
      // kit, logo após Tipo, no formato "1000 X 1000". Cada combinação medida+cor é uma linha
      // própria do grid, com preço próprio.
      base.push({
        field: "medida",
        headerName: "L x H",
        width: 140,
        sortable: false,
        renderCell: (params) => params.row.medida || "—",
      });
    }

    // Superfícies e Acabamentos: só a Unidade, logo após Fornecedor (TP-10 e TP-11).
    if (ehSuperficieOuAcabamento) {
      base.push({ field: "unidadeItem", headerName: "Unidade", width: 110, sortable: false });
    }

    if (ehComponente) {
      base.push(
        { field: "unidadeEmbalagem", headerName: "Emb.", width: 90, sortable: false },
        { field: "quantidadeEmbalagem", headerName: "Qtde.", width: 90, type: "number" }
      );
    }

    // Item 11: a coluna "Situação" deu lugar a "Status" (Ativo/Descontinuado), com filtro.
    base.push({
      field: "status",
      headerName: "Status",
      width: 170,
      sortable: false,
      renderHeader: () => (
        <Select
          size="small"
          variant="standard"
          displayEmpty
          value={filtros.status}
          onChange={(e) => aplicarFiltro("status", e.target.value as number | "")}
          onClick={(e) => e.stopPropagation()}
          sx={{ width: "100%", fontSize: "0.8rem" }}
        >
          <MenuItem value="">Status: todos</MenuItem>
          <MenuItem value={StatusProdutoEnum.Ativo}>
            {STATUS_PRODUTO_LABELS[StatusProdutoEnum.Ativo]}
          </MenuItem>
          <MenuItem value={StatusProdutoEnum.Descontinuado}>
            {STATUS_PRODUTO_LABELS[StatusProdutoEnum.Descontinuado]}
          </MenuItem>
        </Select>
      ),
    });

    // Plano 30/08/2026, Frente 10 (reporte do TI / decisão TP-20): coluna Taxa dos Acabamentos,
    // com o mesmo componente de digitação do Preço e o ícone "i" explicando quando ela incide.
    // Vem ANTES do Preço para o usuário digitar na ordem da planilha (Código, Taxa, Preço).
    if (tipoMaterial === MaterialEnum.ACABAMENTO) {
      base.push({
        field: "taxa",
        headerName: "Taxa",
        width: 190,
        sortable: false,
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span style={{ fontWeight: 500, fontSize: "0.875rem" }}>Taxa</span>
            <Tooltip title="Taxa cobrada caso o lote mínimo não seja atingido">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "#666", cursor: "help" }} />
            </Tooltip>
          </Stack>
        ),
        renderCell: (params) => (
          <CelulaPreco
            valor={Number(params.row.taxa ?? 0)}
            desabilitado={!podeEditarPrecos}
            onConfirmar={(novo) => registrarAlteracaoTaxa(params.row as IItemPrecificacao, novo)}
          />
        ),
      });
    }

    base.push({
      field: "preco",
      // TP-17: "$ Emb." e "$ Item" ficam como estão em Componentes e Vidros; nos tipos
      // novos, onde há um único valor por produto, a coluna se chama "Preço".
      headerName: ehVidro ? "$ m²" : ehComponente ? "$ Emb." : "Preço",
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <CelulaPreco
          valor={Number(params.row.preco ?? 0)}
          desabilitado={!podeEditarPrecos}
          onConfirmar={(novo) => registrarAlteracao(params.row as IItemPrecificacao, novo)}
        />
      ),
    });

    // "$ Item" só existe em Componentes, onde o preço da embalagem se desdobra no unitário.
    if (ehComponente) {
      base.push({
        field: "precoItem",
        headerName: "$ Item",
        width: 170,
        sortable: false,
        renderCell: (params) => (
          <CelulaPreco
            valor={Number(params.row.precoItem ?? 0)}
            desabilitado={!podeEditarPrecos}
            onConfirmar={(novo) => registrarAlteracaoPorItem(params.row as IItemPrecificacao, novo)}
          />
        ),
      });
    }

    return base;
  }, [
    ehVidro,
    ehComponente,
    ehPerfil,
    ehKit,
    ehSuperficieOuAcabamento,
    podeEditarPrecos,
    filtros,
    aplicarFiltro,
    registrarAlteracao,
    registrarAlteracaoPorItem,
  ]);

  const gravar = async () => {
    setSalvando(true);
    try {
      const resultado = await salvarPrecos(idTabelaPreco, Array.from(alteracoes.current.values()));
      if (resultado.sucesso) {
        openNotification({ message: resultado.mensagem || "Preços salvos.", type: "success" });
        await carregar();
      } else {
        openNotification({ message: resultado.mensagem, type: "error" });
      }
    } finally {
      setSalvando(false);
      setModalSalvarAberto(false);
    }
  };

  const handleSalvar = () => {
    if (alteracoes.current.size === 0) return;

    // TP-4: fora de "Em Edição", o SALVAR confirma que a tabela volta para "Em Edição".
    if (situacao !== undefined && situacao !== SituacaoTabelaPrecoEnum.EmEdicao) {
      setModalSalvarAberto(true);
      return;
    }

    gravar();
  };

  const handleExportar = async () => {
    // Plano 30/08/2026, Frente 2 (reporte do TI de 30/08): a exportação ignorava a busca do
    // usuário e sempre baixava a tabela inteira. Vai o mesmo filtro do grid — sem paginação,
    // porque se exporta o resultado completo da busca e não a página visível.
    const linhas = await exportarPrecificacao(idTabelaPreco, {
      pagina: 0,
      tamanhoPagina: 0,
      codigo: filtros.codigo,
      descricao: filtros.descricao,
      fornecedor: filtros.fornecedor,
      linha: filtros.linha,
      tipoKit: filtros.tipoKit,
      status: filtros.status === "" ? null : filtros.status,
      versaoPublicada: modoConsulta,
    });
    if (linhas.length === 0) {
      openNotification({ message: "Não há preços para exportar.", type: "warning" });
      return;
    }
    baixarPlanilhaPrecos(
      linhas,
      precificacao?.tipoMaterial ?? MaterialEnum.COMPONENTE,
      precificacao?.nome || `tabela-precos-${idTabelaPreco}`
    );
  };

  const handleImportar = async (arquivo: File): Promise<ILogImportacaoLinha[] | null> => {
    try {
      const conteudo = await planilhaParaJson(arquivo);

      if (conteudo.length === 0) {
        openNotification({ message: "A planilha está vazia.", type: "warning" });
        return null;
      }

      const resposta = await importarPlanilha({
        idTabelaPreco,
        descricao: precificacao?.nome ?? "",
        referencia: precificacao?.referencia ?? "",
        idTipoMaterial: precificacao?.tipoMaterial ?? MaterialEnum.COMPONENTE,
        arquivo,
        conteudo: JSON.stringify(conteudo),
      });

      if (!resposta.sucesso) {
        openNotification({ message: resposta.mensagem ?? "Erro ao importar a planilha.", type: "error" });
        return null;
      }

      const logs = resposta.log?.logs ?? [];
      if (logs.length > 0) return logs;

      openNotification({ message: "Planilha importada com sucesso.", type: "success" });
      setModalImportarAberto(false);
      await carregar();
      return null;
    } catch (erro) {
      openNotification({ message: "Não foi possível ler a planilha.", type: "error" });
      return null;
    }
  };

  // Plano 14/10/2026, Frente 3: lixeira da "Última planilha importada".
  const handleExcluirPlanilha = async (): Promise<boolean> => {
    const resultado = await desfazerImportacao(idTabelaPreco);
    if (!resultado.sucesso) {
      openNotification({ message: resultado.mensagem, type: "error" });
      return false;
    }

    openNotification({ message: resultado.mensagem || "Planilha excluída.", type: "success" });
    await carregar();
    return true;
  };

  const handleAlterarMassa = async (operacao: OperacaoMassa, valor: number) => {
    const resultado = await alterarPrecosMassa(
      idTabelaPreco,
      operacao,
      valor,
      selecionados.map(String)
    );

    if (resultado.sucesso) {
      openNotification({ message: resultado.mensagem, type: "success" });
      setModalAlterarAberto(false);
      setSelecionados([]);
      await carregar();
    } else {
      openNotification({ message: resultado.mensagem, type: "error" });
    }
  };

  const executarTransicao = async (
    acao: () => Promise<{ sucesso: boolean; mensagem: string }>,
    fecharModal: () => void
  ) => {
    const resultado = await acao();
    if (resultado.sucesso) {
      openNotification({ message: resultado.mensagem, type: "success" });
      fecharModal();
      await carregar();
    } else {
      openNotification({ message: resultado.mensagem, type: "error" });
    }
  };

  const cabecalho = (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => navigate("/tabelas-precos")} size="small" sx={{ color: "#666" }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ color: "#333", fontWeight: 400 }}>
          {precificacao
            ? `Tabela de Preços ${precificacao.nome}${precificacao.empresa ? ` - ${precificacao.empresa}` : ""}`
            : `Tabela #${id}`}
        </Typography>
        {precificacao && (
          <Chip
            size="small"
            label={precificacao.situacaoDescricao}
            sx={{
              backgroundColor: corDaSituacao(precificacao.situacao).bg,
              color: corDaSituacao(precificacao.situacao).fg,
              fontWeight: 500,
            }}
          />
        )}
        {modoConsulta && <Chip size="small" label="Consulta" variant="outlined" />}
      </Stack>

      <Box sx={{ borderBottom: `2px solid ${COR_PRIMARIA}`, pb: 1.5, mb: 2, ml: 4.5 }}>
        <Breadcrumbs separator="›">
          <Link
            underline="hover"
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate("/dashboard");
            }}
            sx={{ fontSize: "0.7rem", cursor: "pointer", textDecoration: "none" }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate("/tabelas-precos");
            }}
            sx={{ fontSize: "0.7rem", cursor: "pointer", textDecoration: "none" }}
          >
            Tabelas de Preços
          </Link>
          <Typography color="text.primary" sx={{ fontSize: "0.7rem" }}>
            {precificacao?.tipoProduto ?? ""}
          </Typography>
        </Breadcrumbs>
      </Box>
    </>
  );

  if (carregando && !precificacao) {
    return (
      <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
        <StackCustom>
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
            <CircularProgress />
          </Stack>
        </StackCustom>
      </DashboardStackCustom>
    );
  }

  if (!precificacao) {
    return (
      <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
        <StackCustom>
          {cabecalho}
          <Alert severity="error">
            {erroCarga ?? "Tabela de preços não encontrada para a empresa logada."}
          </Alert>
        </StackCustom>
      </DashboardStackCustom>
    );
  }

  const mostrarHomologar =
    !modoConsulta && !precificacao.somenteConsulta && situacao === SituacaoTabelaPrecoEnum.EmEdicao;
  const mostrarConcluir =
    !modoConsulta && !precificacao.somenteConsulta && situacao === SituacaoTabelaPrecoEnum.EmHomologacao;
  const mostrarDisponibilizar =
    !modoConsulta && !precificacao.somenteConsulta && situacao === SituacaoTabelaPrecoEnum.Homologada;

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <StackCustom>
        {cabecalho}

        {!precificacao.precificacaoSuportada && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {precificacao.motivoNaoSuportada}
          </Alert>
        )}

        {precificacao.precificacaoSuportada && precificacao.somenteConsulta && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta empresa está liberada apenas para consulta das tabelas de preços.
          </Alert>
        )}

        {precificacao.precificacaoSuportada && modoConsulta && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Consulta da versão disponível para o mercado — apenas produtos com preço.
          </Alert>
        )}

        {precificacao.precificacaoSuportada && podeEditar && planilhaPendente && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {MSG_PLANILHA_PENDENTE}
          </Alert>
        )}

        {precificacao.precificacaoSuportada && (
          <>
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" mb={1.5}>
              {temAlteracoes && (
                <Button
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  onClick={() => setModalDesfazerAberto(true)}
                  sx={{ color: "#FD7E14", borderColor: "#FD7E14" }}
                >
                  Desfazer
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportar}
                sx={{ color: COR_PRIMARIA, borderColor: COR_PRIMARIA }}
              >
                Exportar
              </Button>

              {podeEditar && (
                <>
                  {/* Plano 14/10/2026, Frente 2: edição manual pendente bloqueia a planilha. */}
                  <Tooltip title={edicaoManualPendente ? MSG_EDICAO_MANUAL_PENDENTE : ""}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={edicaoManualPendente}
                        onClick={() => setModalImportarAberto(true)}
                        sx={{ color: COR_PRIMARIA, borderColor: COR_PRIMARIA }}
                      >
                        Importar
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      planilhaPendente
                        ? MSG_PLANILHA_PENDENTE
                        : temAlteracoes
                        ? "Salve as alterações antes de aplicar um reajuste em massa"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<PriceChangeIcon />}
                        disabled={temAlteracoes || Boolean(planilhaPendente)}
                        onClick={() => setModalAlterarAberto(true)}
                        sx={{ color: COR_PRIMARIA, borderColor: COR_PRIMARIA }}
                      >
                        Alterar preços
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    variant="contained"
                    startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!temAlteracoes || salvando}
                    onClick={handleSalvar}
                    sx={{ backgroundColor: COR_PRIMARIA, "&:hover": { backgroundColor: "#2d6f87" } }}
                  >
                    Salvar
                  </Button>
                </>
              )}

              {/* TP-8: "Homologar" só aparece Em Edição e só habilita com ao menos um preço. */}
              {mostrarHomologar && (
                <Tooltip
                  title={
                    precificacao.possuiPrecoGravado
                      ? ""
                      : "Informe ao menos um preço para homologar a tabela"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={temAlteracoes || !precificacao.possuiPrecoGravado}
                      onClick={() => setModalHomologarAberto(true)}
                      sx={{ backgroundColor: "#1565C0", "&:hover": { backgroundColor: "#0d47a1" } }}
                    >
                      Homologar
                    </Button>
                  </span>
                </Tooltip>
              )}

              {mostrarConcluir && (
                <Button
                  variant="contained"
                  disabled={temAlteracoes}
                  onClick={() => setModalConcluirAberto(true)}
                  sx={{ backgroundColor: "#4527A0", "&:hover": { backgroundColor: "#311B92" } }}
                >
                  Concluir homologação
                </Button>
              )}

              {/* Item 17: exclusivo do MASTER, como em Produtos e Aplicabilidades. */}
              {mostrarDisponibilizar && (
                <Tooltip
                  title={isMaster ? "" : "Apenas o usuário MASTER da empresa pode disponibilizar para o mercado"}
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={!isMaster || temAlteracoes}
                      onClick={() => setModalDisponibilizarAberto(true)}
                      sx={{ backgroundColor: "#2E7D32", "&:hover": { backgroundColor: "#1b5e20" } }}
                    >
                      Disponibilizar mercado
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>

            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
              <DataGrid
                rows={itens}
                columns={colunas}
                getRowId={(row) => row.chave}
                autoHeight
                headerHeight={72}
                rowHeight={56}
                checkboxSelection={podeEditarPrecos}
                disableSelectionOnClick
                selectionModel={selecionados}
                onSelectionModelChange={setSelecionados}
                // TP-6: filtro e paginação no servidor.
                paginationMode="server"
                rowCount={totalItens}
                page={pagina}
                pageSize={tamanhoPagina}
                onPageChange={setPagina}
                onPageSizeChange={(novo) => {
                  setTamanhoPagina(novo);
                  setPagina(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                loading={carregando}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#EBF4F9" },
                }}
              />
            </Box>
          </>
        )}
      </StackCustom>

      <ModalAlterarPrecos
        open={modalAlterarAberto}
        quantidadeSelecionada={selecionados.length}
        onClose={() => setModalAlterarAberto(false)}
        onAplicar={handleAlterarMassa}
      />

      <ModalImportar
        open={modalImportarAberto}
        idTabelaPreco={idTabelaPreco}
        tipoMaterial={precificacao.tipoMaterial}
        planilhaPendente={planilhaPendente}
        onClose={() => setModalImportarAberto(false)}
        onImportar={handleImportar}
        onExcluirPlanilha={handleExcluirPlanilha}
      />

      <ModalConfirmacao
        open={modalDesfazerAberto}
        titulo="Descartar alterações?"
        mensagem="As alterações de preço que ainda não foram salvas serão perdidas."
        textoConfirmar="Descartar"
        onClose={() => setModalDesfazerAberto(false)}
        onConfirmar={async () => {
          setModalDesfazerAberto(false);
          await carregar();
        }}
      />

      <ModalConfirmacao
        open={modalSalvarAberto}
        titulo="Confirmar gravação"
        mensagem={MSG_SALVAR_VOLTA_EDICAO}
        textoConfirmar="Continuar"
        onClose={() => setModalSalvarAberto(false)}
        onConfirmar={gravar}
      />

      <ModalConfirmacao
        open={modalHomologarAberto}
        titulo="Confirmar homologação"
        mensagem={MSG_HOMOLOGAR}
        textoConfirmar="Continuar"
        onClose={() => setModalHomologarAberto(false)}
        onConfirmar={() =>
          executarTransicao(() => homologarTabela(idTabelaPreco), () => setModalHomologarAberto(false))
        }
      />

      <ModalConfirmacao
        open={modalConcluirAberto}
        titulo="Confirmar conclusão de homologação"
        mensagem={MSG_CONCLUIR}
        textoConfirmar="Continuar"
        onClose={() => setModalConcluirAberto(false)}
        onConfirmar={() =>
          executarTransicao(
            () => concluirHomologacaoTabela(idTabelaPreco),
            () => setModalConcluirAberto(false)
          )
        }
      />

      <ModalConfirmacao
        open={modalDisponibilizarAberto}
        titulo="Confirmar disponibilização"
        mensagem={MSG_DISPONIBILIZAR}
        textoConfirmar="Continuar"
        onClose={() => setModalDisponibilizarAberto(false)}
        onConfirmar={() =>
          executarTransicao(
            () => disponibilizarTabela(idTabelaPreco),
            () => setModalDisponibilizarAberto(false)
          )
        }
      />
    </DashboardStackCustom>
  );
};

export default TabelaPrecoEdicao;
